.field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
}

/* ── Label ── */
.label {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.125rem;
  color: var(--text-secondary);
}
.required {
  color: var(--error-solid);
}
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

/* ── Trigger (mirrors the Input shell) ── */
.trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--surface);
  box-shadow: var(--shadow-xs);
  color: var(--text-primary);
  font-family: var(--font-family-base);
  text-align: left;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease;
}
.md {
  padding: 0.625rem 0.875rem;
}
.sm {
  padding: 0.5rem 0.875rem;
}

.trigger:focus-visible {
  outline: none;
  border-color: var(--brand-solid);
  box-shadow: var(--focus-ring);
}
.trigger.error {
  border-color: var(--error-solid);
}
.trigger.error:focus-visible {
  box-shadow: var(--focus-ring-error);
}
.trigger:disabled {
  background: var(--surface-secondary);
  cursor: not-allowed;
}

.leading {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
}

.value {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-md-font-size);
  line-height: var(--text-md-line-height);
}
.placeholder {
  color: var(--text-quaternary);
}
.valueLabel {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.valueSupporting {
  flex-shrink: 0;
  color: var(--text-tertiary);
}

.chevron {
  flex-shrink: 0;
  color: var(--text-quaternary);
}

/* ── Inline (bare) trigger — for embedding as an Input prefix / addon ── */
.inlineRoot {
  display: inline-flex;
  align-items: center;
}
.trigger.inline {
  width: auto;
  gap: 0.25rem;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}
.trigger.inline:focus-visible {
  border-color: transparent;
  box-shadow: none;
}
.trigger.inline .value {
  flex: 0 1 auto;
}
.trigger.inline:focus-visible .value {
  border-radius: 0.25rem;
  box-shadow: var(--focus-ring);
}
.searchBox.inline {
  width: auto;
}

/* ── Searchable (combobox input) trigger ── */
.searchBox {
  cursor: text;
}
.searchBox:focus-within {
  outline: none;
  border-color: var(--brand-solid);
  box-shadow: var(--focus-ring);
}
.searchBox.error:focus-within {
  box-shadow: var(--focus-ring-error);
}
.searchIcon {
  flex-shrink: 0;
  color: var(--text-quaternary);
}
.searchInput {
  flex: 1 1 0;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--text-primary);
  font-family: var(--font-family-base);
  font-size: var(--text-md-font-size);
  line-height: var(--text-md-line-height);
}
.searchInput:focus {
  outline: none;
}
.searchInput::placeholder {
  color: var(--text-quaternary);
}
.searchInput:disabled {
  cursor: not-allowed;
}
.shortcut {
  flex-shrink: 0;
  padding: 0.0625rem 0.25rem;
  border: 1px solid var(--border);
  border-radius: 0.25rem;
  color: var(--text-quaternary);
  font-family: var(--font-family-base);
  font-size: var(--text-xs-font-size);
  line-height: var(--text-xs-line-height);
}

/* ── Empty search state ── */
.noResults {
  padding: 0.625rem 0.75rem;
}
.noResultsText {
  color: var(--text-tertiary);
}

.trigger:disabled .value,
.trigger:disabled .chevron {
  color: var(--text-disabled);
}

/* ── Hint / error message ── */
.hint {
  color: var(--text-tertiary);
}
.errorText {
  color: var(--error-solid);
}

@media (forced-colors: active) {
  .trigger:focus-visible {
    outline: 2px solid Highlight;
    outline-offset: 2px;
  }
}
